import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import { withTranslation } from 'react-i18next';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';

import { handleError } from '@mssgme/lib';
import { Tab, Tabs } from '@mssgme/ui';
import StatsList from './StatsList';
import styles from './CardStatistics.scss';

const DAY_TAB = 0;
const WEEK_TAB = 1;
const MONTH_TAB = 2;

const MONTH_MAX_INTERVAL = 2;

const TAB_DAY_MAP = {
    [DAY_TAB]: 'day',
    [WEEK_TAB]: 'week',
    [MONTH_TAB]: 'month',
};

const NavButton = ({ disabled = false, ...rest }) => (
    <a href="#" className={classNames(styles.navButton, { [styles.disabled]: disabled })} {...rest} />
);

NavButton.propTypes = {
    disabled: PropTypes.bool,
};

const Header = (props) => <div className={styles.header} {...props} />;

class CardStatistics extends React.Component {
    static propTypes = {
        statistics: PropTypes.array.isRequired,
        getCardStatistics: PropTypes.func.isRequired,
        t: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);

        const tabIndex = WEEK_TAB;
        const interval = TAB_DAY_MAP[tabIndex];
        const now = moment();

        this.state = {
            tabIndex,
            prev: getPreviousDate(now, interval),
            next: getNextDate(now, interval),
            ...generateDateRange(now, interval),
        };
    }

    componentDidMount() {
        const { from, to } = this.state;

        this.props.getCardStatistics(+from, +to).catch(handleError);
    }

    componentDidUpdate(prevProps, prevState) {
        const { from, to } = this.state;

        if (from != prevState.from || to != prevState.to) {
            this.props.getCardStatistics(+from, +to).catch(handleError);
        }
    }

    handleSelect = (tabIndex) => {
        const interval = TAB_DAY_MAP[tabIndex];
        const now = moment();

        this.setState({
            tabIndex,
            prev: getPreviousDate(now, interval),
            next: getNextDate(now, interval),
            ...generateDateRange(now, interval),
        });
    };

    handleLeftClick = (e) => {
        e.preventDefault();

        this.setState((state) => {
            const interval = TAB_DAY_MAP[state.tabIndex];

            if (state.prev) {
                return {
                    prev: getPreviousDate(state.prev, interval),
                    next: getNextDate(state.prev, interval),
                    ...generateDateRange(state.prev, interval),
                };
            }
        });
    };

    handleRightClick = (e) => {
        e.preventDefault();

        this.setState((state) => {
            const interval = TAB_DAY_MAP[state.tabIndex];

            if (state.next) {
                return {
                    prev: getPreviousDate(state.next, interval),
                    next: getNextDate(state.next, interval),
                    ...generateDateRange(state.next, interval),
                };
            }
        });
    };

    render() {
        const { statistics, t } = this.props;
        const { tabIndex, from, to, prev, next } = this.state;
        const views = statistics.find((item) => item.action === 'page.view');
        const messengers = statistics.filter((item) => item.action === 'messenger.click');
        const links = statistics.filter((item) => item.action === 'link.click');
        const viewsAmount = (views && views.value) || 0;

        return (
            <div>
                <Tabs selected={tabIndex} onSelect={this.handleSelect}>
                    <Tab>{t('CardStatistics.day')}</Tab>
                    <Tab>{t('CardStatistics.week')}</Tab>
                    <Tab>{t('CardStatistics.month')}</Tab>
                </Tabs>
                <Header>
                    <NavButton disabled={!prev} onClick={this.handleLeftClick}>
                        <FaAngleLeft />
                    </NavButton>
                    <p>
                        {tabIndex === DAY_TAB
                            ? from.format('MMM DD')
                            : from.format('MMM DD') + ' - ' + to.format('MMM DD')}
                    </p>
                    <NavButton disabled={!next} onClick={this.handleRightClick}>
                        <FaAngleRight />
                    </NavButton>
                </Header>
                <StatsList expandable={false} stats={viewsAmount} heading={t('pageAnalytics.visits')} />
                <StatsList expandable={false} stats={messengers} heading={t('pageAnalytics.clicks_messengers')} />
                <StatsList expandable={false} stats={links} heading={t('pageAnalytics.clicks_links')} />
            </div>
        );
    }
}

function generateDateRange(dateTime, interval) {
    let from = dateTime.clone().startOf(interval);
    let to = dateTime.clone().endOf(interval);

    return { from, to };
}

function getPreviousDate(dateTime, interval) {
    const { min } = getMinMaxDates(interval);
    let prev = dateTime.clone().subtract(1, interval);

    return prev > min ? prev : null;
}

function getNextDate(dateTime, interval) {
    const { max } = getMinMaxDates(interval);
    let next = dateTime.clone().add(1, interval);

    return next < max ? next : null;
}

function getMinMaxDates(interval) {
    let max = moment().endOf('day');
    let min = moment()
        .subtract(MONTH_MAX_INTERVAL, 'month')
        .startOf('month');

    if (interval !== 'month') min = min.startOf('week');

    return { min, max };
}

export default withTranslation('cards')(CardStatistics);
